import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useStore } from '@nanostores/react'

import type {
  CheckoutPayload,
  PaidPlanId,
  BillingInterval,
  PlanId,
  SpendCapPayload,
  SubscriptionAction,
  WorkspaceBillingState
} from '~/features/billing/types'

import { toast } from '~/elements/Toast'
import { api } from '~/lib/api'
import { FetchError } from '~/lib/fetcher'
import { queryKeys } from '~/lib/queryKeys'
import { trackUpgradeClicked } from '~/lib/analytics'
import { $currentWorkspaceId } from '~/features/workspaces/stores/current'
import { $searchParams, removeSearchParam } from '~/lib/router'
import { usePlatformSettings } from '~/features/auth/hooks/useAuthQueries'
import { useCurrentWorkspaceQuery } from '~/features/workspaces/hooks/useWorkspaceQueries'
import {
  BillingErrorCodes,
  CHECKOUT_POLL_INTERVAL_MS,
  CHECKOUT_POLL_TIMEOUT_MS,
  PLAN_NAMES
} from '~/features/billing/constants'
import { parseBillingError } from '~/features/billing/errors'
import { $checkoutSyncing, openBillingBlockedDialog } from '~/features/billing/stores/blocked'
import { sleep } from '~/features/billing/utils'
import { billingCatalogQueryOptions, billingStateQueryOptions } from '../queries/billingQueries'

export function getBillingErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.') {
  if (error instanceof FetchError) {
    const message = error.data?.message as unknown
    if (Array.isArray(message)) return message.join(', ')
    if (typeof message === 'string' && message) return message
  }
  return error instanceof Error && error.message ? error.message : fallback
}

export function showBillingError(error: unknown) {
  const billingError = parseBillingError(error)
  if (billingError) {
    openBillingBlockedDialog(billingError.reason)
    return
  }
  toast({ variant: 'danger', title: getBillingErrorMessage(error) })
}

export const useBillingCatalogQuery = () => {
  const { data: settings } = usePlatformSettings()
  return useQuery(billingCatalogQueryOptions(settings?.selfHosted))
}

export const useBillingStateQuery = () => {
  const workspaceId = useStore($currentWorkspaceId)
  const { data: settings } = usePlatformSettings()
  return useQuery(billingStateQueryOptions({ workspaceId, selfHosted: settings?.selfHosted }))
}

export const useCurrentWorkspacePlan = () => {
  const { data: workspace, isPending } = useCurrentWorkspaceQuery()
  const { data: state, isLoading: stateLoading } = useBillingStateQuery()
  const { data: catalog } = useBillingCatalogQuery()

  const planId: PlanId = state?.plan ?? workspace?.planId ?? 'free'
  const plan = catalog?.plans.find((p) => p.id === planId)

  return {
    loading: isPending || stateLoading,
    planId,
    plan,
    name: plan?.name ?? PLAN_NAMES[planId] ?? planId,
    isPaid: planId !== 'free',
    state: state ?? undefined,
    catalog
  }
}

export const useRefreshBilling = () => {
  const queryClient = useQueryClient()
  return useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['billing'] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.workspaces.all() })
      ]),
    [queryClient]
  )
}

const isPaid = (state: WorkspaceBillingState | null | undefined) =>
  !!state && (state.plan === 'pro' || state.plan === 'team') && !!state.paddle?.subscriptionId

/**
 * Checkout runs on Paddle's hosted payment page (rushdb.com/pay, an approved
 * domain): the billing service creates the transaction, the browser goes to
 * its payment link, and Paddle returns the customer to
 * /billing?checkout=success. The dashboard never loads Paddle.js.
 */
export const useCheckoutMutation = () => {
  const queryClient = useQueryClient()
  const workspaceId = useStore($currentWorkspaceId)

  return useMutation({
    mutationFn: async (payload: CheckoutPayload) => {
      trackUpgradeClicked({
        currentPlan: queryClient.getQueryData<WorkspaceBillingState | null>(
          queryKeys.billing.state(workspaceId!)
        )?.plan,
        targetPlan: payload.plan,
        billingPeriod: payload.interval === 'year' ? 'annual' : 'monthly'
      })
      const { checkoutUrl } = await api.billing.checkout(payload)
      window.location.assign(checkoutUrl)
    },
    onError: showBillingError
  })
}

/**
 * Back from the payment page with ?checkout=success: poll the billing state
 * until the webhook has applied the subscription, then confirm the new plan.
 */
export const useCheckoutReturn = () => {
  const queryClient = useQueryClient()
  const workspaceId = useStore($currentWorkspaceId)
  const { checkout } = useStore($searchParams)
  const refresh = useRefreshBilling()
  const started = useRef(false)

  useEffect(() => {
    if (checkout !== 'success' || !workspaceId || started.current) return
    started.current = true
    removeSearchParam('checkout')

    void (async () => {
      $checkoutSyncing.set(true)
      const deadline = Date.now() + CHECKOUT_POLL_TIMEOUT_MS
      try {
        while (Date.now() < deadline) {
          const next = await api.billing.getState().catch(() => undefined)
          if (next && next.workspaceId === workspaceId && isPaid(next)) {
            queryClient.setQueryData(queryKeys.billing.state(workspaceId), next)
            await refresh()
            toast({
              title: `You're on ${PLAN_NAMES[next.plan] ?? next.plan}`,
              description: 'Payment successful. Your new plan is active.'
            })
            return
          }
          await sleep(CHECKOUT_POLL_INTERVAL_MS)
        }
        await refresh()
        toast({
          title: 'Payment received',
          description: 'Your plan will update shortly. Refresh this page in a minute if it has not changed.'
        })
      } finally {
        $checkoutSyncing.set(false)
      }
    })()
  }, [checkout, workspaceId, queryClient, refresh])
}

export const useSubscriptionMutation = () => {
  const queryClient = useQueryClient()
  const workspaceId = useStore($currentWorkspaceId)
  const refresh = useRefreshBilling()
  const { mutateAsync: checkout } = useCheckoutMutation()

  return useMutation({
    mutationFn: async (action: SubscriptionAction) => {
      try {
        return await api.billing.updateSubscription(action)
      } catch (error) {
        // 409: no Paddle subscription on this workspace yet, so a plan change is a new checkout.
        if (
          action.action === 'change_plan' &&
          error instanceof FetchError &&
          error.response.status === BillingErrorCodes.Conflict
        ) {
          // The checkout mutation reports its own errors.
          await checkout({ plan: action.plan, interval: action.interval }).catch(() => undefined)
          return null
        }
        throw error
      }
    },
    onSuccess: (state) => {
      if (state) {
        queryClient.setQueryData(queryKeys.billing.state(workspaceId!), state)
      }
      return refresh()
    },
    onError: showBillingError
  })
}

export const useChangePlan = () => {
  const { mutateAsync, isPending } = useSubscriptionMutation()
  return {
    changePlan: (plan: PaidPlanId, interval: BillingInterval) =>
      mutateAsync({ action: 'change_plan', plan, interval }),
    isPending
  }
}

export const useSpendCapMutation = () => {
  const queryClient = useQueryClient()
  const workspaceId = useStore($currentWorkspaceId)

  return useMutation({
    mutationFn: (payload: SpendCapPayload) => api.billing.setSpendCap(payload),
    onSuccess: (state) => {
      queryClient.setQueryData(queryKeys.billing.state(workspaceId!), state)
      toast({ title: 'Spend cap updated' })
    },
    onError: showBillingError
  })
}

type PortalTarget = 'overview' | 'cancel' | 'updatePaymentMethod'

export const useBillingPortal = () => {
  const [isPending, setPending] = useState(false)

  const open = useCallback(async (target: PortalTarget = 'overview') => {
    // Open the tab synchronously inside the click so popup blockers allow it.
    const popup = window.open('', '_blank')
    setPending(true)
    try {
      const urls = await api.billing.createPortalSession()
      const url =
        target === 'cancel' ? urls.cancelUrl
        : target === 'updatePaymentMethod' ? urls.updatePaymentMethodUrl
        : urls.overviewUrl

      if (popup && !popup.closed) {
        popup.opener = null
        popup.location.href = url
      } else {
        window.location.assign(url)
      }
    } catch (error) {
      popup?.close()
      toast({
        variant: 'danger',
        title: "Couldn't open the billing portal",
        description: getBillingErrorMessage(error, 'Please try again in a moment.')
      })
    } finally {
      setPending(false)
    }
  }, [])

  return { open, isPending }
}
